import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import React, {
  Key,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { Size } from "../../../../types";
import { useResizeObserver } from "hooks/useResizeObserver";
import styled from "styled-components";

type Breakpoints = { [key: number]: number };

type RenderGroupArgs<T> = {
  groupKey: string;
  items: T[];
  renderItem: (item: T, indexWithinGroup: number) => React.ReactNode;
};

export const InfiniteScrollList = <T extends Record<string, unknown>>({
  dataSource,
  assetLength = 0,
  children,
  onNext,
  hasMore,
  scrollableTarget,
  loading,
  breakpoints = { 350: 1 },
  groupBy,
  renderGroup,
  rowKey,
}: {
  dataSource: T[];
  rowKey: string | ((item: T) => Key);
  assetLength?: number;
  children: (item: T, index: number) => React.ReactNode;
  onNext?: () => void;
  hasMore: boolean;
  scrollableTarget?: string;
  loading?: boolean;
  breakpoints?: Breakpoints;
  groupBy?: (item: T) => string;
  renderGroup?: (args: RenderGroupArgs<T>) => React.ReactNode;
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const t = useTranslate();
  const handleResize = useCallback((size: Size) => {
    setContainerWidth(size.width);
  }, []);

  const containerRef = useResizeObserver(handleResize);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onNextRef = useRef(onNext);
  // Keep onNext ref up to date without causing re-renders
  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  const getRowKey = (item: T): Key => {
    if (typeof rowKey === "string") {
      return item[rowKey] as Key;
    }
    return rowKey(item);
  };

  // fix for infinit scroll not calling next function on long screens
  // https://github.com/ankeetmaini/react-infinite-scroll-component/issues/380#issuecomment-2567744130
  useLayoutEffect(() => {
    const element = scrollableTarget
      ? document.getElementById(scrollableTarget)
      : containerRef.current;
    if (
      hasMore &&
      element?.firstChild &&
      element.clientHeight >= element.scrollHeight
    ) {
      const resizeObserver = new ResizeObserver((entries) => {
        if (
          entries[0]?.target.clientHeight &&
          entries[0].target.clientHeight <= element.clientHeight &&
          !timeoutRef.current &&
          !loading
        ) {
          timeoutRef.current = setTimeout(() => {
            onNextRef.current?.();
            timeoutRef.current = null;
          }, 2000);
        }
      });

      resizeObserver.observe(element.firstChild as Element);
      return () => {
        resizeObserver.disconnect();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [hasMore, scrollableTarget, containerRef, loading]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <InfiniteScroll
        scrollableTarget={scrollableTarget}
        loader={
          loading && (
            <Flex style={{ width: "100%" }} justify="center" align="center">
              <Typography.Text type="secondary">{t("loading")}</Typography.Text>
            </Flex>
          )
        }
        dataLength={assetLength}
        next={() => onNext?.()}
        hasMore={hasMore}
      >
        {partitionIntoGroups(dataSource, groupBy).map(
          ({ key: groupKey, items, startIndex }) => {
            const renderItem = (item: T, indexWithinGroup: number) =>
              children(item, startIndex + indexWithinGroup);
            if (renderGroup) {
              return (
                <React.Fragment key={groupKey}>
                  {renderGroup({ groupKey, items, renderItem })}
                </React.Fragment>
              );
            }
            return (
              <ResponsiveMasonry
                key={groupKey}
                columnsCountBreakPoints={breakpoints}
                width={containerWidth}
              >
                <StyledMasonry gutter="8px">
                  {items.map((item, indexWithinGroup) => {
                    const key = getRowKey(item);
                    return (
                      <React.Fragment key={key}>
                        {renderItem(item, indexWithinGroup)}
                      </React.Fragment>
                    );
                  })}
                </StyledMasonry>
              </ResponsiveMasonry>
            );
          }
        )}
      </InfiniteScroll>
    </div>
  );
};

const StyledMasonry = styled(Masonry)`
  div:has(> .gallery-item) {
    justify-content: center !important;
  }
`;

type PartitionedGroup<T> = {
  key: string;
  items: T[];
  startIndex: number;
};

const DEFAULT_GROUP_KEY = "__default__";

const partitionIntoGroups = <T,>(
  items: T[],
  groupBy: ((item: T) => string) | undefined
): PartitionedGroup<T>[] => {
  if (!groupBy) {
    return [{ key: DEFAULT_GROUP_KEY, items, startIndex: 0 }];
  }
  const order: string[] = [];
  const buckets = new Map<string, T[]>();
  const startIndices = new Map<string, number>();
  items.forEach((item, index) => {
    const key = groupBy(item);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      startIndices.set(key, index);
      order.push(key);
    }
    buckets.get(key)?.push(item);
  });
  return order.map((key) => ({
    key,
    items: buckets.get(key) ?? [],
    startIndex: startIndices.get(key) ?? 0,
  }));
};
